import type { ConfigField } from '@chukua/common';
import { Form, Input, InputNumber, Switch } from 'antd';
import { Fragment } from 'react';
import { sectionStartingAt } from '../policies/config-field-sections.policy';

interface ConfigFieldsProps {
  fields: ConfigField[];
  /** Names of secrets that already hold a value: their input says "Saved" and may be left blank. */
  secretsSet: string[];
  /** A sentence under a section's heading, by section name. */
  sectionHelp?: Record<string, string>;
}

/**
 * One form item per field of the adapter's config schema, so a new vendor needs no new screen. A secret is a password
 * input that is never prefilled. Fields in a section get its heading above the first of them.
 */
export function ConfigFields({ fields, secretsSet, sectionHelp = {} }: ConfigFieldsProps) {
  return (
    <>
      {fields.map((field, index) => {
        const isSaved = field.secret && secretsSet.includes(field.name);
        const section = sectionStartingAt(fields, index);
        return (
          <Fragment key={field.name}>
            {section && <SectionHeading title={section} help={sectionHelp[section]} />}
            <Form.Item
              name={field.name}
              label={field.label}
              extra={field.help}
              valuePropName={field.type === 'boolean' ? 'checked' : 'value'}
              rules={[{ required: field.required && !isSaved, message: `Enter ${field.label.toLowerCase()}` }]}
            >
              {renderInput(field, isSaved)}
            </Form.Item>
          </Fragment>
        );
      })}
    </>
  );
}

function SectionHeading({ title, help }: { title: string; help: string | undefined }) {
  return (
    <div className="mt-8 mb-4 border-t border-line pt-5">
      <h3 className="text-sm font-semibold text-fg">{title}</h3>
      {help && <p className="mt-1 text-sm text-muted">{help}</p>}
    </div>
  );
}

function renderInput(field: ConfigField, isSaved: boolean) {
  if (field.type === 'boolean') return <Switch />;
  if (field.type === 'number') return <InputNumber className="w-full" />;
  if (field.secret) return <Input.Password autoComplete="new-password" placeholder={isSaved ? 'Saved — leave blank to keep it' : undefined} />;
  return <Input />;
}
